import {EventProcessStageTitle, Tag} from '@hconnect/common/components'
import {getAssigneesValue} from '@hconnect/common/utils'
import {DateFormat} from '@hconnect/uikit'
import {Grid} from '@mui/material'
import {getEquipmentLabelDetails, EventStatusLabel} from '@widgets/common-shift-event'
import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {DalogActionDto} from '../../types'

import {TaskDescription} from './TaskDescription'

type Props = {
  dalogAction: DalogActionDto
  plantId: string
  plantName: string
  timezone: string
}

export const DalogActionDetails = ({dalogAction, plantId, plantName, timezone}: Props) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const translate = (key: string) => t(key)
  const noneOfThatLabel = translate('task.noneOfThat')

  const assigneesValue: string = useMemo(() => {
    const assignees = getAssigneesValue(dalogAction.assignees, t)
    return assignees ? assignees : noneOfThatLabel
  }, [noneOfThatLabel, dalogAction, t])

  const functionalLocation = dalogAction.dalogProperties?.functionalLocation ?? noneOfThatLabel
  const dalogProcessStage = dalogAction.dalogProperties?.processStage ?? noneOfThatLabel

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Tag label={translate('task.label.status')} data-test-id="task-details-status">
            <EventStatusLabel status={dalogAction.status} />
          </Tag>
        </Grid>
        <Grid item md={12}>
          <Tag label={translate('task.label.priority')} data-test-id="task-details-priority">
            {dalogAction.priority
              ? translate(`dalogActions.priority.${dalogAction.priority}`)
              : noneOfThatLabel}
          </Tag>
        </Grid>

        <Grid item md={12}>
          <Tag label={translate('task.label.dueDate')} data-test-id="task-details-dueDate">
            {dalogAction.dueDate ? (
              <DateFormat date={dalogAction.dueDate} timezone={timezone} />
            ) : (
              noneOfThatLabel
            )}
          </Tag>
        </Grid>
        <Grid item md={12}>
          <Tag label={translate('task.label.plant')} data-test-id="task-details-plant">
            {plantName}
          </Tag>
        </Grid>
        <Grid item xs={12}>
          <Tag
            data-test-id="task-details-mainEquipment"
            label={translate('task.label.mainEquipment')}
          >
            {getEquipmentLabelDetails(dalogAction.mainEquipment, false)}
          </Tag>
        </Grid>
        <Grid item xs={12}>
          <Tag data-test-id="task-details-equipment" label={translate('task.label.equipment')}>
            {getEquipmentLabelDetails(dalogAction.equipment)}
          </Tag>
        </Grid>
        <Grid item xs={12}>
          <Tag
            label={translate('dalogActions.label.dalogProcessStage')}
            data-test-id="task-details-dalogProcessStage"
          >
            {dalogProcessStage}
          </Tag>
        </Grid>
        <Grid item xs={12}>
          <Tag
            label={translate('dalogActions.label.processStage')}
            data-test-id="task-details-processStage"
          >
            {dalogAction.processStage ? (
              <EventProcessStageTitle plantId={plantId} stage={dalogAction.processStage} />
            ) : (
              noneOfThatLabel
            )}
          </Tag>
        </Grid>
        <Grid item xs={12}>
          <Tag
            label={translate('dalogActions.label.dalogActionFunctionalLocation')}
            data-test-id="task-details-dalogFunctionalLocation"
          >
            {functionalLocation}
          </Tag>
        </Grid>
        <Grid item xs={12}>
          <Tag data-test-id="task-details-assignees" label={translate('task.label.assignee')}>
            {assigneesValue}
          </Tag>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Tag
            label={translate('task.label.description')}
            descriptionSx={{fontWeight: 400, fontSize: 14}}
            data-test-id="task-details-description"
          >
            <TaskDescription description={dalogAction.description} />
          </Tag>
        </Grid>
      </Grid>
    </>
  )
}
